// Tutorial slide 5/5. Mock flashcard that reveals pinyin + meaning on tap,
// mirroring the real Library → Flashcards mode. Closes the loop:
// match → chat → look up → save → review.

import { useState } from 'react';
import { ChevronRight, Eye, EyeOff, Library } from 'lucide-react';
import { C } from '@/styles/tokens';

interface FlashcardDemoProps {
  onContinue: () => void;
}

export function FlashcardDemo({ onContinue }: FlashcardDemoProps) {
  const [revealed, setRevealed] = useState(false);

  return (
    <div className="flex-1 flex flex-col">
      <div className="ff-display text-3xl mb-2" style={{ color: C.ink }}>
        Review anytime.
      </div>
      <div className="ff-body text-[14px] leading-relaxed mb-8" style={{ color: C.inkLight }}>
        Your <Library size={12} className="inline align-baseline" aria-hidden /> Library doubles as a flashcard deck.
        Tap the card to reveal pinyin and meaning.
      </div>

      <div className="flex-1 flex flex-col items-center justify-center">
        <button
          type="button"
          onClick={() => setRevealed((v) => !v)}
          className="relative w-[240px] h-[300px] rounded-3xl overflow-hidden anim-scale-in transition-colors"
          style={{
            background: revealed
              ? `linear-gradient(135deg, ${C.blush}, ${C.coral})`
              : `linear-gradient(135deg, ${C.paper}, ${C.cream})`,
            border: `1px solid ${C.mist}`,
            boxShadow: '0 18px 40px -16px rgba(26,22,20,0.25)',
          }}
          aria-label={revealed ? 'Hide pinyin and meaning' : 'Reveal pinyin and meaning'}
          aria-expanded={revealed}
        >
          {!revealed ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center anim-ink-bloom">
              <span className="ff-zh-serif text-7xl font-bold" style={{ color: C.ink }}>
                咖啡
              </span>
              <span
                className="ff-body text-[11px] uppercase tracking-[0.25em] mt-6 inline-flex items-center gap-1"
                style={{ color: C.inkSofter }}
              >
                <Eye size={10} aria-hidden /> Tap to reveal
              </span>
            </div>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center anim-ink-bloom">
              <span className="ff-zh-serif text-4xl font-semibold mb-2" style={{ color: C.ink }}>
                咖啡
              </span>
              <span className="ff-body text-[16px] italic mb-1" style={{ color: C.vermillionDeep }}>
                kāfēi
              </span>
              <span className="ff-body text-[14px]" style={{ color: C.ink }}>
                coffee
              </span>
              <span
                className="ff-body text-[10px] uppercase tracking-[0.25em] mt-6 inline-flex items-center gap-1"
                style={{ color: C.inkSofter }}
              >
                <EyeOff size={10} aria-hidden /> Tap to hide
              </span>
            </div>
          )}
        </button>
      </div>

      <button
        type="button"
        onClick={onContinue}
        className="mt-6 py-3 rounded-full ff-body font-semibold text-white inline-flex items-center justify-center gap-2"
        style={{ background: C.vermillion }}
      >
        Ready to meet someone
        <ChevronRight size={16} aria-hidden />
      </button>
    </div>
  );
}
